import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCompanies } from '@/api/insights';

/**
 * Global client scope (dev/planning/general.md §4.9 Phase B) — the client picked in the header.
 * Every scope-aware page and list hook narrows to this Autotask company.
 *
 *  - Lives until the browser closes (sessionStorage `cosmos:scope:client`), by decision: scoping
 *    that survives overnight means people working filtered without noticing.
 *  - Recent picks are kept longer (localStorage `cosmos:scope:recent`, last 6).
 *  - Arriving with `?scope=<id>` sets it (shareable scoped links), then the param is stripped.
 *    (Not `?client=`: RMM list views already use that for their own client filter.)
 */

export interface ClientScope { companyId: number; name: string }

interface Ctx {
  scope: ClientScope | null;
  setScope: (s: ClientScope | null) => void;
  recent: ClientScope[];
}

const SCOPE_KEY = 'cosmos:scope:client';
const RECENT_KEY = 'cosmos:scope:recent';
const RECENT_MAX = 6;

function read<T>(storage: () => Storage, key: string, fallback: T): T {
  try { const v = storage().getItem(key); return v ? JSON.parse(v) as T : fallback; } catch { return fallback; }
}
function write(storage: () => Storage, key: string, value: unknown) {
  try { if (value == null) storage().removeItem(key); else storage().setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

const ClientScopeContext = createContext<Ctx>({ scope: null, setScope: () => {}, recent: [] });

export function ClientScopeProvider({ children }: { children: React.ReactNode }) {
  const [scope, setScopeState] = useState<ClientScope | null>(() => read(() => sessionStorage, SCOPE_KEY, null));
  const [recent, setRecent] = useState<ClientScope[]>(() => read(() => localStorage, RECENT_KEY, []));

  const setScope = useCallback((s: ClientScope | null) => {
    setScopeState(s);
    write(() => sessionStorage, SCOPE_KEY, s);
    if (s) {
      setRecent((prev) => {
        const next = [s, ...prev.filter((r) => r.companyId !== s.companyId)].slice(0, RECENT_MAX);
        write(() => localStorage, RECENT_KEY, next);
        return next;
      });
    }
  }, []);

  // ?scope=<id> → scope (name resolved from the company index), then drop the param.
  const loc = useLocation();
  const navigate = useNavigate();
  const pending = (() => {
    const v = new URLSearchParams(loc.search).get('scope');
    const n = v != null && /^\d+$/.test(v) ? Number(v) : null;
    return n;
  })();
  const companies = useCompanies({ enabled: pending != null });
  useEffect(() => {
    if (pending == null) return;
    const known = companies.data?.companies.find((c) => c.id === pending);
    if (!known && companies.isLoading) return;
    setScope({ companyId: pending, name: known?.name ?? `Company #${pending}` });
    const params = new URLSearchParams(loc.search);
    params.delete('scope');
    navigate({ pathname: loc.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
  }, [pending, companies.data, companies.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Browser tab shows the client so a scoped window is recognisable.
  useEffect(() => {
    const base = document.title.replace(/ · .*$/, '');
    document.title = scope ? `${base} · ${scope.name}` : base;
  }, [scope]);

  const value = useMemo(() => ({ scope, setScope, recent }), [scope, setScope, recent]);
  return <ClientScopeContext.Provider value={value}>{children}</ClientScopeContext.Provider>;
}

export function useClientScope() {
  return useContext(ClientScopeContext);
}

/**
 * For list hooks/pages: `inScope(companyId)` is true for everything when no client is picked, and
 * only for the picked company otherwise (unmapped records — null — drop out while scoped).
 */
export function useScopeFilter() {
  const { scope } = useClientScope();
  const id = scope?.companyId ?? null;
  const inScope = useCallback((companyId: number | null | undefined) => id == null || companyId === id, [id]);
  return { companyId: id, inScope };
}

/**
 * A TanStack Query `select` that narrows a list response to the picked client — only when the
 * caller opted in (`scoped`) and a client is picked; otherwise undefined (no select, raw data).
 * `narrow` must be a pure function of its arguments (it's defined once per hook).
 */
export function useScopedSelect<T>(scoped: boolean | undefined, narrow: (data: T, inScope: (companyId: number | null | undefined) => boolean) => T) {
  const { companyId, inScope } = useScopeFilter();
  return useMemo(
    () => (scoped && companyId != null ? (d: T) => narrow(d, inScope) : undefined),
    [scoped, companyId, inScope], // eslint-disable-line react-hooks/exhaustive-deps
  );
}
